/**
 * Hash dashboard sidebar — ported from the office system so a customer
 * using both products gets one mental model.
 *
 * Layout:
 *   ┌─────────────────────────────────┐
 *   │ logo · org · language switcher  │  ← header
 *   │ [ חיפוש מהיר ⌘K ]               │  ← QuickSearch trigger
 *   ├─────────────────────────────────┤
 *   │ MAIN                            │
 *   │  • Dashboard          ⌘⇧D       │
 *   │  • Chat               ⌘⇧M       │
 *   │  …                              │
 *   │ STATEMENTS                      │
 *   │  …                              │
 *   ├─────────────────────────────────┤
 *   │ [admin items]                   │  ← footer
 *   │ [ logout ]                      │
 *   └─────────────────────────────────┘
 *
 * Behaviour:
 *  - Collapsible: 280px ↔ 68px, state persists in localStorage.
 *  - Light surface, primary-tinted active row with side-rail accent.
 *  - Per-row keyboard shortcut hint (right side).
 *  - Right-edge toggle button (chevron in / out) sits half-outside
 *    the drawer on the trailing edge.
 *  - Active match is the longest prefix so /expenses/category/foo
 *    still highlights /expenses, not a different "/" item.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getBranding } from '../../api/branding';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckIcon from '@mui/icons-material/Check';
import ViewListIcon from '@mui/icons-material/ViewList';
import LayersIcon from '@mui/icons-material/Layers';
import Collapse from '@mui/material/Collapse';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { SECTIONS, FOOTER_ITEMS, pickActive, flattenItems, type NavItem } from './navCatalog';
import { useAuthStore } from '../../stores/authStore';
import { useUiPrefsStore } from '../../stores/uiPrefsStore';
import LanguageSwitcher from './LanguageSwitcher';
import QuickSearch from './QuickSearch';
import { buildActionToCombo, formatCombo } from './shortcutCatalog';
import {
  isFavorite, pinFavorite, unpinFavorite, moveFavorite, useFavorites,
  FAVORITES_MAX,
} from './favoritesStore';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 68;
const COLLAPSED_STORAGE_KEY = 'hash_sidebar_collapsed';

// Per-section accent colors — each nav category gets its own vivid
// hue so the sidebar reads as colorful, icon-forward groups rather
// than a flat blue list. Mirrors the redesign that landed in the
// office system AppShell. Icons render in tinted "chips" of these
// colors; the active row fills solid.
const SECTION_ACCENTS: Record<string, string> = {
  main:       '#4F46E5',  // indigo  — daily-driver / inbox / chat / audits
  statements: '#7C3AED',  // violet  — formal financial reports
  books:      '#059669',  // emerald — bookkeeping
  operations: '#D97706',  // amber   — operational tracking
  analysis:   '#DB2777',  // pink    — drill-downs + AI tools
  system:     '#475569',  // slate   — account & settings
};
const FAVORITES_ACCENT = '#F59E0B';     // amber for the favorites group
const FOOTER_ACCENT = '#64748B';        // slate for the (now-empty) footer
function accentFor(key: string): string {
  return SECTION_ACCENTS[key] || '#2563EB';
}

const COLORS = {
  primary:       '#2563EB',
  primaryLight:  '#DBEAFE',
  primaryDeep:   '#1B2A6B',
  surface:       '#FFFFFF',
  surfaceAlt:    '#F8FAFC',
  border:        '#E2E8F0',
  text:          '#0F172A',
  textSecondary: '#64748B',
  textMuted:     '#94A3B8',
};

export default function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.is_admin ?? false;

  // Per-org branding — when the active org has a logo/display name
  // set, the sidebar header renders those instead of the default
  // Maven mark. Same data flows into the print footer.
  const { data: branding } = useQuery({
    queryKey: ['org', 'branding'],
    queryFn: getBranding,
    staleTime: 5 * 60_000,
  });

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'; }
    catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Filter by admin gate and skip empty sections.
  const visibleSections = useMemo(() =>
    SECTIONS.map((s) => ({
      ...s,
      items: s.items.filter((it) => !it.adminOnly || isAdmin),
    })).filter((s) => s.items.length > 0),
  [isAdmin]);

  const visibleFooter = useMemo(() =>
    FOOTER_ITEMS.filter((it) => !it.adminOnly || isAdmin),
  [isAdmin]);

  const allRoutes = useMemo(() =>
    flattenItems(true).map((i) => i.to),
  []);
  const activeTo = pickActive(location.pathname, allRoutes);

  const actionToCombo = useMemo(buildActionToCombo, []);
  const handleLogout = () => { logout(); navigate('/login'); };

  // Favorites: rendered above the SECTIONS as a "מועדפים" group. Each
  // pinned item is also still shown in its native section — pinning is
  // a shortcut, not a move.
  const favorites = useFavorites();
  const itemByPath = useMemo(() => {
    const m = new Map<string, NavItem>();
    for (const it of flattenItems(true)) m.set(it.to, it);
    return m;
  }, []);
  const favoriteItems = useMemo(() =>
    favorites
      .map((f) => {
        if (!f.action_key.startsWith('nav:')) return null;
        return itemByPath.get(f.action_key.slice('nav:'.length)) ?? null;
      })
      .filter((x): x is NavItem => !!x),
  [favorites, itemByPath]);

  // Collapsible sections — port from office. The single open section's
  // key (or null) lives in localStorage via uiPrefsStore. The user
  // picks one of two render styles: accordion (in-place expand) or
  // flyout (Popper anchored to the section header). The flyout is
  // dismissed on route change or click-away.
  const submenuStyle = useUiPrefsStore((s) => s.submenuStyle);
  const setSubmenuStyle = useUiPrefsStore((s) => s.setSubmenuStyle);
  const openSectionKey = useUiPrefsStore((s) => s.openSectionKey);
  const toggleSection = useUiPrefsStore((s) => s.toggleSection);
  const setOpenSectionKey = useUiPrefsStore((s) => s.setOpenSectionKey);
  const [paletteAnchor, setPaletteAnchor] = useState<HTMLElement | null>(null);
  const [flyoutAnchor, setFlyoutAnchor] = useState<{ el: HTMLElement; key: string } | null>(null);
  const closeFlyout = () => setFlyoutAnchor(null);

  // Auto-expand the section that contains the current route so it's
  // always visible without an extra click. Also push the visited path
  // onto the recent rail (de-duplicated, capped at 12). Run on route
  // change.
  const trackVisit = useUiPrefsStore((s) => s.trackVisit);
  useEffect(() => {
    if (activeTo) {
      const owning = visibleSections.find((s) => s.items.some((i) => i.to === activeTo));
      if (owning && openSectionKey !== owning.key) {
        setOpenSectionKey(owning.key);
      }
    }
    closeFlyout();
    trackVisit(location.pathname);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Right-click context menu — pin/unpin/reorder a nav row.
  const [ctx, setCtx] = useState<{ anchor: {top:number;left:number}; item: NavItem } | null>(null);
  const handleContext = (item: NavItem, e: React.MouseEvent) => {
    e.preventDefault();
    setCtx({ anchor: { top: e.clientY, left: e.clientX }, item });
  };
  const closeCtx = () => setCtx(null);

  return (
    <Box
      component="nav"
      className="no-print"
      sx={{
        position: 'fixed',
        top: 0,
        // Use logical positioning so the sidebar lands on the correct
        // inline-start edge of the screen — right in RTL, left in LTR.
        // The stylis RTL plugin flips raw `right`/`left` to the wrong
        // side, which is what put the sidebar on the wrong edge before.
        insetInlineStart: 0,
        height: '100vh',
        width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
        zIndex: 1200,
        // Hide on small screens by sliding it out of view. The Cmd-K
        // command palette (or future hamburger) provides the alternate
        // navigation surface there.
        display: { xs: 'none', md: 'flex' },
        backgroundColor: COLORS.surface,
        // Hairline on the page-facing edge of the sidebar.
        borderInlineEnd: `1px solid ${COLORS.border}`,
        display: 'flex', flexDirection: 'column',
        overflow: 'visible',
        transition: theme.transitions.create('width', { duration: 200 }),
      }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box sx={{
        px: collapsed ? 1 : 2, py: 1.5,
        display: 'flex', alignItems: 'center',
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderBottom: `1px solid ${COLORS.border}`,
        minHeight: 64,
      }}>
        <Tooltip arrow title="לוח הבקרה הראשי">
          {branding?.logo_url ? (
            <Box
              component={Link}
              to="/dashboard"
              sx={{
                width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                textDecoration: 'none', cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={branding.logo_url}
                alt={branding.display_name ?? 'logo'}
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Box component={Link} to="/dashboard" sx={{
              width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
              background: branding?.accent_color
                ? `linear-gradient(135deg, ${branding.accent_color}cc 0%, ${branding.accent_color} 100%)`
                : 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', textDecoration: 'none', cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(37,99,235,0.35)',
              fontWeight: 900, fontSize: 19, fontFamily: '"Rubik","Heebo",sans-serif',
              letterSpacing: '-0.02em',
              transition: 'transform .12s ease, box-shadow .12s ease',
              '&:hover': {
                transform: 'scale(1.06)',
                boxShadow: '0 4px 12px rgba(37,99,235,0.55)',
              },
            }}>M</Box>
          )}
        </Tooltip>
        {!collapsed && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontWeight: 800, fontSize: 16, lineHeight: 1.2,
                color: COLORS.text, letterSpacing: '-0.02em',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{branding?.display_name || 'BookWise'}</Typography>
              <Typography sx={{
                fontSize: 11.5, color: COLORS.textSecondary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {user?.full_name || user?.email || ''}
              </Typography>
            </Box>
            <Tooltip title="התאמת תפריט">
              <IconButton
                size="small"
                onClick={(e) => setPaletteAnchor(e.currentTarget)}
                sx={{ color: COLORS.textSecondary }}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <LanguageSwitcher />
          </>
        )}
      </Box>

      {/* Quick personalize popover — same content as the
          /personalize page but reachable in one click from the
          sidebar header, matching the office system's UX. */}
      <Menu
        anchorEl={paletteAnchor}
        open={!!paletteAnchor}
        onClose={() => setPaletteAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ px: 2, py: 1, color: COLORS.textMuted, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' as const }}>
          סגנון תפריט
        </Box>
        <MenuItem
          onClick={() => { setSubmenuStyle('accordion'); setPaletteAnchor(null); }}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            {submenuStyle === 'accordion'
              ? <CheckIcon fontSize="small" sx={{ color: COLORS.primary }} />
              : <ViewListIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary="אקורדיון"
            secondary="קטגוריה אחת נפתחת בכל פעם"
            secondaryTypographyProps={{ sx: { fontSize: 11 } }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => { setSubmenuStyle('flyout'); setPaletteAnchor(null); }}
        >
          <ListItemIcon>
            {submenuStyle === 'flyout'
              ? <CheckIcon fontSize="small" sx={{ color: COLORS.primary }} />
              : <LayersIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary="פלייאאוט"
            secondary="כרטיס צף — מאיר עיניים"
            secondaryTypographyProps={{ sx: { fontSize: 11 } }}
          />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          component={Link}
          to="/personalize"
          onClick={() => setPaletteAnchor(null)}
        >
          <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="כל ההעדפות…" />
        </MenuItem>
      </Menu>

      {/* ── Quick search ──────────────────────────────────────── */}
      {!collapsed && <QuickSearch />}

      {/* ── Nav body (scrolls) ────────────────────────────────── */}
      <Box sx={{
        flex: 1, overflowY: 'auto',
        px: collapsed ? 0.5 : 1, py: 1.25,
      }}>
        {favoriteItems.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            {collapsed ? (
              <Divider sx={{ my: 0.75, mx: 1 }} />
            ) : (
              <Typography sx={{
                px: 1.5, mt: 0.25, mb: 0.5,
                fontSize: 10.5, fontWeight: 800,
                letterSpacing: '0.10em',
                color: COLORS.textMuted, textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <StarIcon sx={{ fontSize: 12, color: '#F59E0B' }} />
                מועדפים
              </Typography>
            )}
            {favoriteItems.map((it) => (
              <NavRow
                key={`fav-${it.to}`} item={it}
                active={activeTo === it.to}
                collapsed={collapsed}
                combo={actionToCombo[`nav:${it.to}`]}
                accent={FAVORITES_ACCENT}
                onContextMenu={(e) => handleContext(it, e)}
              />
            ))}
          </Box>
        )}

        {visibleSections.map((s) => {
          const open = openSectionKey === s.key;
          const containsActive = !!activeTo && s.items.some((i) => i.to === activeTo);
          const accent = accentFor(s.key);
          // Collapsed sidebar shows the flat list with dividers — the
          // toggle UI requires labels and would only get in the way.
          if (collapsed) {
            return (
              <Box key={s.key} sx={{ mb: 0.5 }}>
                <Divider sx={{ my: 0.75, mx: 1 }} />
                {s.items.map((it) => (
                  <NavRow
                    key={it.to} item={it}
                    active={activeTo === it.to}
                    collapsed={collapsed}
                    combo={actionToCombo[`nav:${it.to}`]}
                    accent={accent}
                    onContextMenu={(e) => handleContext(it, e)}
                  />
                ))}
              </Box>
            );
          }
          const accordionOpen = submenuStyle === 'accordion' && open;
          const headerActive = containsActive || open || flyoutAnchor?.key === s.key;
          return (
            <Box key={s.key} sx={{ mb: 0.25 }}>
              {/* Section header — click toggles either the accordion
                  or the flyout depending on `submenuStyle`. */}
              <Box
                data-nav-section-header
                onClick={(e) => {
                  if (submenuStyle === 'flyout') {
                    const el = e.currentTarget as HTMLElement;
                    if (flyoutAnchor?.key === s.key) {
                      closeFlyout();
                    } else {
                      setFlyoutAnchor({ el, key: s.key });
                    }
                  } else {
                    toggleSection(s.key);
                  }
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1, py: 0.6, mx: 0.5,
                  mt: 1,
                  borderRadius: 1.75,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: headerActive ? accent : COLORS.textSecondary,
                  bgcolor: headerActive ? `${accent}12` : 'transparent',
                  '&:hover': { bgcolor: `${accent}14` },
                  transition: 'background-color 120ms, color 120ms',
                }}
              >
                {s.sectionIcon && (
                  <Box sx={{
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 26, height: 26, borderRadius: 1.5,
                    background: accent, color: '#fff',
                    boxShadow: `0 2px 6px ${accent}55`,
                    '& svg': { fontSize: 16 },
                  }}>
                    {s.sectionIcon}
                  </Box>
                )}
                <Typography sx={{
                  flexGrow: 1,
                  fontSize: 11.5, fontWeight: 800,
                  letterSpacing: '0.06em',
                  color: headerActive ? accent : COLORS.text,
                }}>{t(s.title, { defaultValue: s.title })}</Typography>
                <Typography sx={{
                  fontSize: 10, fontWeight: 800,
                  color: accent,
                  px: 0.75, py: 0.125, borderRadius: 99,
                  bgcolor: `${accent}1A`,
                }}>
                  {s.items.length}
                </Typography>
                {submenuStyle === 'flyout'
                  ? <KeyboardArrowLeftIcon sx={{ fontSize: 18, color: 'inherit' }} />
                  : (open
                      ? <ExpandLessIcon sx={{ fontSize: 18, color: 'inherit' }} />
                      : <ExpandMoreIcon sx={{ fontSize: 18, color: 'inherit' }} />)}
              </Box>
              {/* Accordion body — only rendered in accordion style. */}
              <Collapse in={accordionOpen} timeout={180} unmountOnExit>
                <Box>
                  {s.items.map((it) => (
                    <NavRow
                      key={it.to} item={it}
                      active={activeTo === it.to}
                      collapsed={collapsed}
                      combo={actionToCombo[`nav:${it.to}`]}
                      accent={accent}
                      onContextMenu={(e) => handleContext(it, e)}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Flyout panel — only in `flyout` submenu style. */}
      {!collapsed && submenuStyle === 'flyout' && flyoutAnchor && (() => {
        const sec = visibleSections.find((s) => s.key === flyoutAnchor.key);
        if (!sec) return null;
        const flyoutAccent = accentFor(sec.key);
        return (
          <Popper
            open
            anchorEl={flyoutAnchor.el}
            placement="left-start"
            modifiers={[
              { name: 'offset', options: { offset: [0, 12] } },
              { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
              { name: 'flip', enabled: false },
            ]}
            sx={{ zIndex: 1300 }}
          >
            <Fade in timeout={180}>
              <Paper elevation={8} sx={{
                width: 340,
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                borderRadius: 2.5,
                border: `1px solid ${COLORS.border}`,
                bgcolor: COLORS.surface,
                boxShadow: '0 20px 50px -12px rgba(15, 23, 42, 0.30)',
              }}>
                <ClickAwayListener
                  onClickAway={(event: MouseEvent | TouchEvent) => {
                    const target = event.target as HTMLElement | null;
                    if (target && target.closest('[data-nav-section-header]')) {
                      return;
                    }
                    closeFlyout();
                  }}
                >
                  <Box>
                    <Box sx={{
                      px: 2.5, py: 2,
                      borderBottom: `1px solid ${COLORS.border}`,
                      background: `linear-gradient(135deg, ${flyoutAccent} 0%, ${flyoutAccent}CC 100%)`,
                      color: 'white',
                      display: 'flex', alignItems: 'center', gap: 1.25,
                    }}>
                      {sec.sectionIcon && (
                        <Box sx={{
                          width: 38, height: 38, borderRadius: 1.5,
                          bgcolor: 'rgba(255,255,255,0.18)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          '& svg': { fontSize: 22, color: 'white' },
                        }}>
                          {sec.sectionIcon}
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'white' }}>
                          {t(sec.title, { defaultValue: sec.title })}
                        </Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.78)' }}>
                          {sec.items.length} פריטים
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={closeFlyout}
                        sx={{ color: 'rgba(255,255,255,0.85)',
                              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.12)' } }}>
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      {sec.items.map((it) => (
                        <NavRow
                          key={it.to} item={it}
                          active={activeTo === it.to}
                          collapsed={false}
                          combo={actionToCombo[`nav:${it.to}`]}
                          accent={flyoutAccent}
                          onContextMenu={(e) => handleContext(it, e)}
                        />
                      ))}
                    </Box>
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Fade>
          </Popper>
        );
      })()}

      {/* ── Footer (admin + logout) ───────────────────────────── */}
      <Divider />
      <Box sx={{ p: collapsed ? 0.5 : 1 }}>
        {visibleFooter.map((it) => (
          <NavRow
            key={it.to} item={it}
            active={activeTo === it.to}
            collapsed={collapsed}
            combo={actionToCombo[`nav:${it.to}`]}
            accent={FOOTER_ACCENT}
            compact
          />
        ))}
        <NavLogout onClick={handleLogout} collapsed={collapsed} />
      </Box>

      {/* ── Context menu (pin / unpin / reorder) ───────────────── */}
      <Menu
        open={!!ctx?.anchor}
        onClose={closeCtx}
        anchorReference="anchorPosition"
        anchorPosition={ctx?.anchor ?? undefined}
      >
        {ctx?.item && (() => {
          const item = ctx.item;
          const ak = `nav:${item.to}`;
          const pinned = isFavorite(ak);
          return [
            pinned ? (
              <MenuItem key="unpin" onClick={() => { closeCtx(); unpinFavorite(ak); }}>
                <ListItemIcon><StarBorderIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="הסר מהמועדפים" />
              </MenuItem>
            ) : (
              <MenuItem
                key="pin"
                onClick={() => { closeCtx(); pinFavorite(ak); }}
                disabled={favorites.length >= FAVORITES_MAX}
              >
                <ListItemIcon><StarIcon fontSize="small" sx={{ color: '#F59E0B' }} /></ListItemIcon>
                <ListItemText
                  primary="הוסף למועדפים"
                  secondary={favorites.length >= FAVORITES_MAX ? `מקסימום ${FAVORITES_MAX} מועדפים` : undefined}
                />
              </MenuItem>
            ),
            pinned && (
              <MenuItem key="up" onClick={() => { closeCtx(); moveFavorite(ak, -1); }}>
                <ListItemIcon><ArrowUpwardIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="העבר מעלה" />
              </MenuItem>
            ),
            pinned && (
              <MenuItem key="down" onClick={() => { closeCtx(); moveFavorite(ak, 1); }}>
                <ListItemIcon><ArrowDownwardIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="העבר מטה" />
              </MenuItem>
            ),
          ].filter(Boolean);
        })()}
      </Menu>

      {/* ── Collapse toggle ───────────────────────────────────── */}
      <Tooltip
        title={collapsed ? 'הרחב תפריט' : 'צמצם תפריט'}
        placement="top"
      >
        <IconButton
          onClick={toggleCollapsed}
          size="small"
          aria-label={collapsed ? 'הרחב תפריט' : 'צמצם תפריט'}
          sx={{
            position: 'absolute', top: 22,
            // Toggle sits half-outside on the *page-facing* edge of the
            // sidebar — that's `insetInlineEnd` (end of inline axis ≈
            // toward the page in both RTL and LTR).
            insetInlineEnd: -14, zIndex: 1300,
            width: 28, height: 28,
            backgroundColor: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
            color: COLORS.textSecondary,
            '&:hover': { backgroundColor: COLORS.primaryLight, color: COLORS.primary },
          }}
        >
          {/* In RTL the page is to the left of the sidebar; chevron
              should point that way (collapsed ↔ expand toward page). */}
          {collapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function NavRow({
  item, active, collapsed = false, combo, compact = false, accent = COLORS.primary, onContextMenu,
}: {
  item: NavItem;
  active: boolean;
  collapsed?: boolean;
  combo?: string;
  compact?: boolean;
  /** Section accent color — drives the icon chip + active tint. */
  accent?: string;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const label = t(item.label, { defaultValue: item.label });
  const row = (
    <Box
      component={Link} to={item.to}
      onContextMenu={onContextMenu}
      sx={{
        display: 'flex', alignItems: 'center',
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 0 : 1.5,
        py: compact ? 0.9 : 1.1,
        mx: 0.25, my: 0.2,
        borderRadius: 2,
        textDecoration: 'none',
        color: active ? accent : COLORS.text,
        position: 'relative',
        background: active ? `${accent}16` : 'transparent',
        transition: 'background .15s ease, color .15s ease, transform .15s ease',
        '&:hover': {
          background: active ? `${accent}22` : `${accent}0F`,
        },
        '&:active': { transform: 'scale(0.985)' },
        '&::before': active && !collapsed ? {
          content: '""', position: 'absolute',
          insetInlineStart: 0, top: 7, bottom: 7,
          width: 3, borderRadius: 999,
          background: accent,
        } : undefined,
      }}
    >
      <Box sx={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: collapsed ? 34 : 30, height: collapsed ? 34 : 30,
        borderRadius: 1.75,
        background: active ? accent : `${accent}1A`,
        color: active ? '#fff' : accent,
        boxShadow: active ? `0 4px 10px ${accent}55` : 'none',
        transition: 'background .15s ease, color .15s ease, box-shadow .15s ease',
        '& svg': { fontSize: collapsed ? 21 : 19 },
      }}>{item.icon}</Box>
      {!collapsed && (
        <>
          <Typography sx={{
            flex: 1, minWidth: 0, fontSize: 13.5,
            fontWeight: active ? 800 : 600,
            color: active ? accent : COLORS.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{label}</Typography>
          {combo && (
            <Box sx={{
              px: 0.65, py: 0.1, borderRadius: 0.75,
              fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700,
              color: COLORS.textSecondary, opacity: 0.85,
              backgroundColor: 'rgba(15,23,42,0.06)',
            }}>{formatCombo(combo)}</Box>
          )}
        </>
      )}
    </Box>
  );
  const tip = combo ? `${label} (${formatCombo(combo)})` : label;
  return collapsed
    ? <Tooltip title={tip} placement="left" arrow>{row}</Tooltip>
    : row;
}

function NavLogout({ onClick, collapsed = false }: { onClick: () => void; collapsed?: boolean }) {
  const { t } = useTranslation();
  const label = t('auth.logout', { defaultValue: 'התנתק' });
  const row = (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center',
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 0 : 1.5,
        py: 0.9, mx: 0.25, my: 0.15,
        borderRadius: 2, cursor: 'pointer',
        color: '#B91C1C',
        '&:hover': { backgroundColor: '#FEE2E2' },
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22,
        '& svg': { fontSize: 20 },
      }}><LogoutIcon /></Box>
      {!collapsed && (
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{label}</Typography>
      )}
    </Box>
  );
  return collapsed
    ? <Tooltip title={label} placement="left" arrow>{row}</Tooltip>
    : row;
}

export function getSidebarWidth(): number {
  // Best-effort sync read so AppShell can reserve the right margin
  // without prop-drilling. Updates flow back via a custom event below.
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
      ? DRAWER_WIDTH_COLLAPSED
      : DRAWER_WIDTH;
  } catch {
    return DRAWER_WIDTH;
  }
}

export {
  DRAWER_WIDTH as SIDEBAR_WIDTH,
  DRAWER_WIDTH_COLLAPSED as SIDEBAR_WIDTH_COLLAPSED,
  COLLAPSED_STORAGE_KEY as SIDEBAR_COLLAPSED_STORAGE_KEY,
};
